import { SlotInfo } from 'react-big-calendar';
import { DateTime } from 'luxon';

import toast from 'react-hot-toast';

import {
  BookingState,
  getTypeOfUser,
  ICalendarEventType,
  PharmacistLegendVariant,
  PharmacyLegendVariant,
  RecurrenceActions,
  TypeOfUser,
} from '@pharmaplan/common';

import { ScreenTypes } from '../helpers/Constants';
import { renderScreen, setOpen } from '../actions/drawerActions';
import { getEnableAddVacations } from '../selectors/calendarSelector';

import { useAppDispatch } from './useAppDispatch';
import { useAppSelector } from './useAppSelector';
import useSelfService from './useSelfService';

interface IOpenDrawer {
  eventIds?: Array<string>;
  recurrenceType?: RecurrenceActions;
  recurrenceId?: string;
  type?: ICalendarEventType;
  bookingState?: BookingState;
  preventInteraction?: boolean;
  date?: string;
  isAutoSyncIDMap?: Record<string, boolean>;
  openDetails?: boolean;
  fromNotification?: boolean;
  longTermShiftContractId?: string;
  professionalId?: string;
}

const { pharmacy, pharmacist, technician, student } = TypeOfUser;
const { availability, vacation, workshift } = PharmacistLegendVariant;
const { posted, booked, requested } = PharmacyLegendVariant;

const {
  workshiftSummary,
  updateWorkshift,
  updateAvailability,
  serviceWorkshiftDetails,
  addWorkshift,
  addAvailability,
  createVacation,
  workshiftList,
} = ScreenTypes;

const useDrawerNavigation = () => {
  const dispatch = useAppDispatch();

  const userType = useAppSelector(getTypeOfUser);
  const isVacations = useAppSelector(getEnableAddVacations);
  const { isSelfService } = useSelfService();

  const isPharmacy = userType === pharmacy;

  const handleScreenTypes = (
    type: ICalendarEventType,
    date?: string,
    openDetails?: boolean,
    fromNotification?: boolean,
  ) => {
    const handleWorkshiftScreen = {
      screenType: openDetails ? updateWorkshift : workshiftSummary,
      screenNumber: openDetails ? 2 : 5,
    };

    const handleSelfServiceScreen = {
      screenType: workshiftSummary,
      screenNumber: 3,
    };

    const isSelfAndNotification = isSelfService && fromNotification;

    const { screenType, screenNumber } = handleWorkshiftScreen;

    const requestScreenType = isSelfAndNotification
      ? handleSelfServiceScreen.screenType
      : screenType;
    const requestScreenNumber = isSelfAndNotification
      ? handleSelfServiceScreen.screenNumber
      : screenNumber;

    switch (type) {
      case availability:
        return {
          screenType: updateAvailability,
          screenNumber: 1,
        };
      case posted:
        return {
          screenType,
          screenNumber,
        };
      case requested:
        return isPharmacy
          ? {
            screenType: requestScreenType,
            screenNumber: requestScreenNumber,
          }
          : {
            screenType: serviceWorkshiftDetails,
            screenNumber: 1,
          };
      case booked:
        return isPharmacy
          ? {
            screenType: workshiftSummary,
            screenNumber: fromNotification || openDetails ? 3 : 5,
          }
          : {
            screenType: serviceWorkshiftDetails,
            screenNumber: 1,
          };
      case vacation:
        return {
          screenType: createVacation,
          screenNumber: 1,
        };
      case workshift:
        if (date) {
          return {
            screenType: workshiftList,
            screenNumber: 1,
          };
        }
        return {
          screenType: serviceWorkshiftDetails,
          screenNumber: 1,
        };

      default:
        return {
          screenType: serviceWorkshiftDetails,
          screenNumber: 1,
        };
    }
  };

  const pharmacistAddDrawerType = isVacations
    ? createVacation
    : addAvailability;

  const handleAddDrawerScreenType = {
    [pharmacy]: addWorkshift,
    [pharmacist]: pharmacistAddDrawerType,
    [technician]: pharmacistAddDrawerType,
    [student]: pharmacistAddDrawerType,
  };

  const openAddDrawer = (slot: SlotInfo) => {
    dispatch(setOpen(true));

    dispatch(
      renderScreen({
        date:
          DateTime.fromJSDate(slot.start).toISO({
            includeOffset: false,
          }) ?? '',
        screenType:
          handleAddDrawerScreenType[
            userType as keyof typeof handleAddDrawerScreenType
          ],
        screenNumber: 1,
      }),
    );
  };

  const openAddDefaultDrawer = () => {
    dispatch(setOpen(true));

    dispatch(
      renderScreen({
        screenType:
          handleAddDrawerScreenType[
            userType as keyof typeof handleAddDrawerScreenType
          ],
        screenNumber: 1,
      }),
    );
  };

  const openDrawer = ({
    eventIds,
    recurrenceType,
    recurrenceId,
    type,
    bookingState,
    preventInteraction,
    date,
    openDetails,
    isAutoSyncIDMap,
    fromNotification,
    longTermShiftContractId,
    professionalId,
  }: IOpenDrawer) => {
    toast.dismiss();
    dispatch(
      renderScreen({
        recType: recurrenceType,
        date,
        recurrenceId,
        type,
        eventId: eventIds?.[0],
        eventIds,
        bookingState,
        pharmacistId: professionalId,
        extraParameters: {
          isAutoSyncIDMap,
          preventInteraction,
          longTermShiftContractId,
        },
        ...handleScreenTypes(
          type ?? PharmacistLegendVariant.workshift,
          date,
          openDetails,
          fromNotification,
        ),
      }),
    );
    dispatch(setOpen(true));
  };

  return { openDrawer, openAddDrawer, openAddDefaultDrawer };
};

export default useDrawerNavigation;
